import React, { useMemo } from 'react';
import axios from 'axios';
import config from '../../config';
import { getRegion } from '../../helpers';
import { useAuth0 } from '@auth0/auth0-react';

const BASE_URL = `${config.api.url}api/`;
const BASE_URL_V2 = `${config.api.url}api/v2/`;

async function sendRequest({ getAccessToken, uri, method, data, useV2, removeRegion = false }) {
    const region = getRegion();
    let token = '';
    // Only get the access token for auth api calls
    if (getAccessToken) {
        token = await getAccessToken();
    }

    let url = `${useV2 ? BASE_URL_V2 : BASE_URL}${region}/${uri}`;
    if (removeRegion) {
        url = `${useV2 ? BASE_URL_V2 : BASE_URL}${uri}`;
    }

    const headers = {
        'content-type': 'application/json'
    };

    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    let requestConfig = {
        method,
        url: url,
        headers: headers
    };

    if (method === 'post' || method === 'put' || data) {
        requestConfig = { ...requestConfig, data };
    }

    return axios(requestConfig);
}

async function downloadRequest(getAccessToken, uri, method, fileName) {
    let token = '';

    // Only get the access token for auth api calls
    if (getAccessToken) {
        token = await getAccessToken();
    }

    const headers = {
        'content-type': 'application/json',
        responseType: 'application/octet-stream'
    };

    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    return axios({
        method,
        url: uri,
        dataType: 'binary',
        processData: false,
        responseType: 'arraybuffer',
        headers: headers
    }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
    });
}

function get(getAccessToken, uri, id, useV2 = false) {
    return sendRequest({ getAccessToken, uri: `${uri}${id !== undefined ? `/${id}` : ''}`, method: 'get', useV2 });
}

function getRegionless(getAccessToken, uri, id, useV2 = false) {
    return sendRequest({ getAccessToken, uri: `${uri}${id !== undefined ? `/${id}` : ''}`, method: 'get', useV2, removeRegion: true });
}

function getAll(getAccessToken, uri, page, queryStr = '', useV2 = false) {
    return sendRequest({ getAccessToken, uri: `${uri}/?page=${page || 1}${queryStr}`, method: 'get', useV2 });
}

function getAllSorted(getAccessToken, uri, page, queryStr = '', sortByKey = '', sortIsDescending = true, useV2 = false) {
    return sendRequest({ getAccessToken, uri: `${uri}/?page=${page || 1}${queryStr}&sortByKey=${sortByKey}&sortIsDescending=${sortIsDescending}`, method: 'get', useV2 });
}

function post(getAccessToken, uri, data, useV2 = false) {
    return sendRequest({ getAccessToken, uri, method: 'post', data, useV2 });
}

function postRegionless(getAccessToken, uri, data, useV2 = false) {
    return sendRequest({ getAccessToken, uri, method: 'post', data, useV2, removeRegion: true });
}

function put(getAccessToken, uri, id, data, useV2 = false) {
    return sendRequest({ getAccessToken, uri: `${uri}/${id}`, method: 'put', data, useV2 });
}

function del(getAccessToken, uri, id, useV2 = false) {
    return sendRequest({ getAccessToken, uri: `${uri}/${id}`, method: 'delete', useV2 });
}

const getApiCalls = getAccessTokenSilently => {
    return {
        getAllEmployers: (page, queryStr) => getAll(getAccessTokenSilently, 'employer', page, queryStr, true),
        getEmployer: id => get(getAccessTokenSilently, 'employer', id, true),
        updateEmployer: (id, data) => put(getAccessTokenSilently, 'employer', id, data, true),
        uploadContentFile: data => post(getAccessTokenSilently, `contentfile`, data),

        getAllJobs: (page, queryStr) => getAll(getAccessTokenSilently, 'job', page, queryStr),
        getJob: id => get(getAccessTokenSilently, 'job', id),
        getAllUsers: (page, queryStr) => getAll(getAccessTokenSilently, 'user', page, queryStr),
        getUser: id => get(getAccessTokenSilently, 'user', id),
        addUser: data => post(getAccessTokenSilently, 'user', data),
        updateUser: (id, data) => put(getAccessTokenSilently, 'user', id, data),
        deleteUser: id => del(getAccessTokenSilently, 'user', id),
        getAllApplications: (page, queryStr, sortByKey, sortIsDescending) =>
            getAllSorted(getAccessTokenSilently, 'application/manage', page, queryStr, sortByKey, sortIsDescending),
        getApplication: id => get(getAccessTokenSilently, 'application', id),
        updateApplication: (id, data) => put(getAccessTokenSilently, 'application/manage', id, data),

        getFile: (fileId, fileName) => downloadRequest(getAccessTokenSilently, `${BASE_URL}download/${fileId}`, 'post', fileName),
        getAllJobFilters: () => get(getAccessTokenSilently, 'job/jobfilters'),

        // JobAutomation
        getAllJobAutomations: page => getAll(getAccessTokenSilently, 'jobautomation', page),
        getJobAutomation: employerId => get(getAccessTokenSilently, 'jobautomation', employerId),
        sendJobAutomationDryRun: payload => postRegionless(getAccessTokenSilently, 'scraper/dry-run/jobAutomation', payload),
        getJobAutomationDryRunResult: id => getRegionless(getAccessTokenSilently, `scraper/dry-run?id=${id}`),
        runJobAutomation: employerId => postRegionless(getAccessTokenSilently, `scraper/runsite?employerId=${employerId}`),
        toggleJobAutomationStatus: (employerId, isDisabled) => post(getAccessTokenSilently, `jobautomation/toggle-status/${employerId}/${isDisabled}`)
    };
};

const ApiClientContext = React.createContext();

const ApiClientProvider = ({ children }) => {
    const { getAccessTokenSilently } = useAuth0();
    const apiClient = useMemo(() => {
        return getApiCalls(getAccessTokenSilently);
    }, [getAccessTokenSilently]);

    return <ApiClientContext.Provider value={{ apiClient }}>{children}</ApiClientContext.Provider>;
};

export { ApiClientProvider, ApiClientContext, getApiCalls };
